// CSS filter chain → one colour matrix (W3C Filter Effects, the same maths the
// browser uses for `filter:` / Canvas2D `ctx.filter`). The live stage applies the
// booth's colour filter in its last WebGL pass with this matrix instead of a CSS
// filter on the canvas (which made the compositor re-filter the whole canvas every
// frame on Android); the saved strip keeps `ctx.filter` with the same string, so the
// photo looks like the preview.

/** Row-major 3×3 matrix + offset: c' = M·c + o (sRGB 0..1). */
export interface ColorMatrix { m: number[]; o: [number, number, number] }

export const IDENTITY: ColorMatrix = { m: [1, 0, 0, 0, 1, 0, 0, 0, 1], o: [0, 0, 0] };

const mul = (a: number[], b: number[]) => {
  const r = new Array(9).fill(0);
  for (let i = 0; i < 3; i++) for (let j = 0; j < 3; j++) for (let k = 0; k < 3; k++) r[i * 3 + j] += a[i * 3 + k] * b[k * 3 + j];
  return r;
};
const apply = (m: number[], v: [number, number, number]): [number, number, number] => [
  m[0] * v[0] + m[1] * v[1] + m[2] * v[2],
  m[3] * v[0] + m[4] * v[1] + m[5] * v[2],
  m[6] * v[0] + m[7] * v[1] + m[8] * v[2],
];

function step(fn: string, x: number): ColorMatrix | null {
  switch (fn) {
    case 'brightness': return { m: [x, 0, 0, 0, x, 0, 0, 0, x], o: [0, 0, 0] };
    case 'contrast': { const o = 0.5 - 0.5 * x; return { m: [x, 0, 0, 0, x, 0, 0, 0, x], o: [o, o, o] }; }
    case 'saturate': return {
      m: [0.213 + 0.787 * x, 0.715 - 0.715 * x, 0.072 - 0.072 * x,
        0.213 - 0.213 * x, 0.715 + 0.285 * x, 0.072 - 0.072 * x,
        0.213 - 0.213 * x, 0.715 - 0.715 * x, 0.072 + 0.928 * x], o: [0, 0, 0],
    };
    case 'grayscale': { const s = 1 - Math.min(1, x); return {
      m: [0.2126 + 0.7874 * s, 0.7152 - 0.7152 * s, 0.0722 - 0.0722 * s,
        0.2126 - 0.2126 * s, 0.7152 + 0.2848 * s, 0.0722 - 0.0722 * s,
        0.2126 - 0.2126 * s, 0.7152 - 0.7152 * s, 0.0722 + 0.9278 * s], o: [0, 0, 0],
    }; }
    case 'sepia': { const s = 1 - Math.min(1, x); return {
      m: [0.393 + 0.607 * s, 0.769 - 0.769 * s, 0.189 - 0.189 * s,
        0.349 - 0.349 * s, 0.686 + 0.314 * s, 0.168 - 0.168 * s,
        0.272 - 0.272 * s, 0.534 - 0.534 * s, 0.131 + 0.869 * s], o: [0, 0, 0],
    }; }
    default: return null;
  }
}

/** Parses e.g. "brightness(1.12) contrast(0.9) saturate(0.92)"; unknown functions are ignored. */
export function cssFilterMatrix(css: string | undefined | null): ColorMatrix {
  if (!css || css.trim() === 'none') return IDENTITY;
  let acc = IDENTITY;
  for (const [, fn, arg, pct] of css.matchAll(/([a-z-]+)\(\s*([-\d.]+)(%?)\s*\)/g)) {
    const x = pct ? parseFloat(arg) / 100 : parseFloat(arg);
    const s = step(fn, x);
    if (!s) continue;
    // later filters apply to the output of earlier ones: (S, so) ∘ (A, ao) = (S·A, S·ao + so)
    const o = apply(s.m, acc.o);
    acc = { m: mul(s.m, acc.m), o: [o[0] + s.o[0], o[1] + s.o[1], o[2] + s.o[2]] };
  }
  return acc;
}

export const isIdentity = (c: ColorMatrix) =>
  c.m.every((v, i) => Math.abs(v - IDENTITY.m[i]) < 1e-6) && c.o.every((v) => Math.abs(v) < 1e-6);

/** CPU reference (tests): applies the matrix to one sRGB colour, clamped like the shader. */
export function gradeColor(c: ColorMatrix, rgb: [number, number, number]): [number, number, number] {
  const v = apply(c.m, rgb);
  return [0, 1, 2].map((i) => Math.max(0, Math.min(1, v[i] + c.o[i]))) as [number, number, number];
}
